// src/components/ui/InputOtp.jsx
import React, { useRef } from "react";

/** 6-digit OTP – centered boxes, AfriMed styling + paste support */
export default function InputOtp({
  value = "",
  onChange,
  length = 6,
  tone = "auth",
  onComplete,
  autoFocus = false,
  align = "center",
}) {
  const refs = useRef([]);
  const isSurfaceTone = tone === "surface";
  const inputTextColor = isSurfaceTone
    ? "rgb(var(--clr-on-surface))"
    : "rgb(4,8,18)";
  const inputBackground = isSurfaceTone
    ? "rgb(var(--clr-surface-cont-highest))"
    : "rgb(var(--afrimed-auth-input-surface))";
  const inputShadow = isSurfaceTone
    ? "0 0 0 1px rgba(var(--clr-outline-variant), 0.95) inset"
    : "0 6px 14px rgba(15,23,42,0.06)";

  const raw = String(value || "").slice(0, length);
  const chars = Array.from({ length }, (_, i) => raw[i] || "");

  function commit(next) {
    const clean = next.replace(/\D/g, "").slice(0, length);
    onChange?.(clean);
    if (clean.length === length) {
      onComplete?.(clean);
    }
    // focus last filled box
    const idx = Math.min(clean.length, length) - 1;
    if (idx >= 0) refs.current[idx]?.focus();
  }

  function normalizePastedOtp(text) {
    const rawText = String(text || "").trim();
    if (!rawText) return "";
    if (/[A-Za-z]/.test(rawText)) return "";
    if (!/^[\d\s-]+$/.test(rawText)) return "";
    const digits = rawText.replace(/\D/g, "");
    return digits.length === length ? digits : "";
  }

  function setAt(index, ch) {
    const clean = ch.replace(/\D/g, "");
    const current = raw.split("");
    current[index] = clean || "";
    commit(current.join(""));
  }

  function handleKey(index, e) {
    if (e.key === "Backspace" && !chars[index] && index > 0) {
      refs.current[index - 1]?.focus();
    }
  }

  function handlePaste(e) {
    const text =
      e.clipboardData?.getData("text") ??
      window.clipboardData?.getData("Text") ??
      "";
    e.preventDefault();
    const pastedOtp = normalizePastedOtp(text);
    if (pastedOtp) commit(pastedOtp);
  }

  return (
    <div
      className={`afrimed-otp flex items-center ${align === "start" ? "justify-start" : "justify-center"} gap-3`}
      style={{
        ["--afrimed-otp-bg"]: inputBackground,
        ["--afrimed-otp-fg"]: inputTextColor,
        ["--afrimed-otp-shadow"]: inputShadow,
      }}
    >
      {chars.map((c, i) => (
        <input
          key={i}
          ref={(el) => (refs.current[i] = el)}
          type="tel"
          inputMode="numeric"
          maxLength={1}
          value={c}
          autoFocus={autoFocus && i === 0}
          onChange={(e) => {
            const ch = e.target.value.replace(/\D/g, "").slice(-1);
            setAt(i, ch);
            if (ch && i < length - 1) refs.current[i + 1]?.focus();
          }}
          onKeyDown={(e) => handleKey(i, e)}
          // allow pasting the whole code into ANY box
          onPaste={handlePaste}
          className="
            w-12 h-12 text-center rounded-[10px]
            text-[18px] font-medium
            outline-none
          "
          style={{
            background: inputBackground,
            color: inputTextColor,
            WebkitTextFillColor: inputTextColor,
            caretColor: inputTextColor,
            boxShadow: inputShadow,
            appearance: "none",
            WebkitAppearance: "none",
          }}
        />
      ))}
    </div>
  );
}
